import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { CATALOGUES, LOCALES, type Locale, type MessageKey } from './catalogue';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ `useT()`. One call per string, with a literal key.                        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **The same provider as `apps/web`'s, over a DIFFERENT catalogue — and the duplication is the
 * decision, not an oversight.** `decisions.md` S-6 keeps the two applications separate; a shared
 * `MessageKey` union is what the outgoing project had, and R-1 lists *"two `TranslationKey`
 * unions"* among the things that made its boundary expensive. Sharing the union would mean every
 * operator string is a key the product's typecheck also carries, and vice versa. The provider is
 * forty lines; the catalogue is the part that must not be shared.
 *
 * ⚠️ **CALL `t()` WITH A LITERAL, ONE CALL PER BRANCH.** `t(keyFor(status))` is invisible to any
 * check that reads the source for literal arguments — every branch's entry then reports as an
 * orphan, and the fix somebody reaches for is *deleting the entry*. `MessageKey` makes a typo a
 * type error; only a literal makes the key findable.
 *
 * ⚠️ **Interpolation is `{name}`, and a missing value is LOUD.** A silent `{name}` left in the
 * output is a defect that reaches a screenshot: it reads as a template that failed, and nobody can
 * tell whether the value was empty or the key was wrong. So an unreplaced placeholder throws in
 * development and renders the key in production, which is ugly on purpose.
 */

interface I18n {
  readonly locale: Locale;
  readonly t: (key: MessageKey, values?: Readonly<Record<string, string | number>>) => string;
  /**
   * ⚠️⚠️ **SWITCHES THE LANGUAGE IN PLACE, AND THE OLD SWITCH NEVER DID.** The console's menu wrote
   * `locale` to storage and reloaded — and nothing anywhere read it back, so the page came back in
   * the language it left in. The mockup switches instantly from a segmented control in the
   * sidebar; this is what that control calls. Absent where nobody supplied one (a test rendering a
   * single locale), and the control is then not drawn.
   */
  readonly setLocale?: (next: Locale) => void;
}

const I18nContext = createContext<I18n | undefined>(undefined);

/** Turns `{a} and {b}` into a string, or complains. */
function format(
  template: string,
  key: string,
  values: Readonly<Record<string, string | number>> | undefined,
): string {
  if (!template.includes('{')) return template;

  return template.replace(/\{(\w+)\}/g, (_match, name: string) => {
    const value = values?.[name];
    if (value === undefined) {
      /* ⚠️ Not a silent fallback. A message with an unfilled placeholder is wrong in a way that
       * looks like a data problem, and the cheapest place to find out is here. */
      if (import.meta.env.DEV) {
        throw new Error(`the message ${key} needs a value for {${name}}`);
      }
      return `{${name}}`;
    }
    return String(value);
  });
}

export function I18nProvider({
  locale,
  onLocaleChange,
  children,
}: {
  readonly locale: Locale;
  readonly onLocaleChange?: (next: Locale) => void;
  readonly children: ReactNode;
}) {
  const value = useMemo<I18n>(
    () => ({
      locale,
      t: (key, values) => format(CATALOGUES[locale][key], key, values),
      ...(onLocaleChange === undefined ? {} : { setLocale: onLocaleChange }),
    }),
    [locale, onLocaleChange],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const value = useContext(I18nContext);
  /* ⚠️ Throws rather than defaulting to Hungarian. A component rendered outside the provider would
   * otherwise work perfectly in one locale and be untranslatable in the other, and the missing
   * provider would surface as a mysteriously monolingual screen. */
  if (value === undefined) {
    throw new Error('useI18n was called outside I18nProvider');
  }
  return value;
}

export function useT(): I18n['t'] {
  return useI18n().t;
}

/**
 * ⚠️ **`navigator.languages` is typed non-nullable and is not always present.** jsdom and older
 * engines leave it undefined, so the types say the `??` below is unnecessary and the runtime
 * disagrees — hence reading it through `unknown` rather than suppressing the lint rule, which
 * would also suppress a real one later.
 */
function browserLanguages(): readonly string[] {
  /* Read as `unknown` off a widened `globalThis`, because the DOM types promise `navigator` and
   * `navigator.languages` both exist and neither promise holds in every runtime this code runs in. */
  const languages = (globalThis as { navigator?: { languages?: unknown } }).navigator?.languages;
  return Array.isArray(languages) ? (languages as readonly string[]) : [];
}

/**
 * The locale to start in.
 *
 * ⚠️ **Hungarian is the DEFAULT, not the fallback**, per `decisions.md` R-6 — the product is
 * Hungarian first, and an unrecognised `navigator.language` lands on HU rather than on English.
 */
export function preferredLocale(languages: readonly string[] = browserLanguages()): Locale {
  for (const language of languages) {
    const short = language.slice(0, 2).toLowerCase();
    const match = LOCALES.find((locale) => locale === short);
    if (match !== undefined) return match;
  }
  return 'hu';
}

/**
 * ⚠️ **Exported so a helper OUTSIDE a component can take `t` as a parameter.** A screen with a
 * per-branch label function (`viewLabel(t, view)`) needs to type that parameter, and the alternative
 * — `ReturnType<typeof useT>` at each call site — spells the same type four different ways.
 *
 * The per-branch shape exists because `i18n:check` reads **literal** keys only: `t(keyFor(view))` is
 * invisible to it, so every branch's entry reports as an orphan and the fix people reach for is
 * deleting the entry.
 */
export type Translate = I18n['t'];

export { LOCALES, type Locale, type MessageKey } from './catalogue';
